import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { DriversFilter } from "@/components/common/section-components/filters/DriversFilter";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { isEmpty } from "@/utils/query-string";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { NoData } from "@/components/common/section-components/NoData";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { DriversActions } from "@/components/common/section-components/action/DriversActions";
import {
  DriverListEntity,
  DriversListResponse,
  driversFilterSchema,
} from "./schema/drivers.schema";
import { GetDriversListProps } from "@/services/drivers.service";
import { driversHeaderElements } from "./schema/header-elements";
import {
  clearFiltersHandler,
  fetchDriversHandler,
  fetchEquiposHandler,
  handleDeleteDriversHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";

export const DriversPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, [isActive]);

  const navigate = useNavigate();
  const handleAddDriver = () => {
    navigate(AppRoutes.DRIVER_CREATION);
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.DRIVER_EDIT.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: any) => (
    <DriversActions handleDetail={handleDetail} item={item} />
  );

  const { hasWriteAccess, fetchPermissions } = useRoles();

  const { control, setValue, getValues, handleSubmit } = useForm({
    resolver: zodResolver(driversFilterSchema),
  });

  const [loadingDrivers, setLoadingDrivers] = useState<boolean>(false);
  const [drivers, setDrivers] = useState<DriversListResponse | null>(null);
  const [loadingDeleteDrivers, setLoadingDeleteDrivers] =
    useState<boolean>(false);
  const [page, setPage] = useState(1);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<
    Partial<GetDriversListProps> | undefined
  >();

  const [equipmentOptions, setEquipmentOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [loadingAuxOptions, setLoadingAuxOptions] = useState<boolean>(false);

  const [selection, setSelection] = useState<string[]>([]);
  const hasSelection = selection.length > 0;
  const indeterminate = hasSelection && selection.length < selection?.length;

  const { open: openConfirm, setOpen: setOpenConfirmModal } = useDisclosure();

  const fetchDrivers = async (
    filters?: Partial<GetDriversListProps>,
    showLoad = true,
  ) => {
    await fetchDriversHandler({
      setLoadingDrivers,
      setDrivers,
      navigate,
      fetchPermissions,
      showLoad,
      filters,
    });
  };

  const submitFilters = async () => {
    await submitFiltersHandler({
      event: getValues(),
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      setPage,
      fetchDrivers,
    });
  };

  const clearFilters = async () => {
    await clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setOpenFilters,
      setValue,
      submitFilters: async () => submitFiltersHandler({
        event: getValues(),
        setCurrentFilters,
        setFiltersApplied,
        setOpenFilters,
        setPage,
        fetchDrivers,
      }),
    });
  };

  useEffect(() => {
    if (!isActive) return;

    fetchEquiposHandler({
      setLoadingAuxOptions,
      setEquipmentOptions,
    });
  }, [isActive]);

  useEffect(() => {
    if (isActive) {
      fetchDrivers();
    }
  }, [isActive]);

  const values = useWatch({ control });
  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    fetchDrivers({ ...currentFilters, "P.Pagina": nextPage }, true);
  };

  const handleDelete = async () => {
    await handleDeleteDriversHandler({
      selection,
      setLoadingDeleteDrivers,
      setOpenConfirm: setOpenConfirmModal,
      fetchDrivers,
      setSelection,
    });
  };

  return (
    <Flex flexDir="column" paddingTop="20px" data-testid="drivers-page">
      <SectionHeader
        title={"Maestro de fleteros"}
        addButtonLabel="Agregar"
        deleteButtonLabel="Eliminar"
        handleAdd={handleAddDriver}
        handleDelete={() => setOpenConfirmModal(true)}
        disableDelete={!selection?.length}
        loading={loadingDrivers}
        showButtons={hasWriteAccess(PermissionCode.DRIVERS)}
      />

      <DriversFilter
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        disableFilters={loadingDrivers}
        loadingFilters={loadingDrivers}
        filtersApplied={filtersApplied}
        disableClear={loadingDrivers}
        disableApply={loadingDrivers || filtersEmpty || loadingAuxOptions}
        equipmentOptions={equipmentOptions}
      />

      <Flex flexDir="column">
        {Boolean(!loadingDrivers && !drivers?.listado?.length) && <NoData />}
        {Boolean(!loadingDrivers && drivers?.listado?.length) && (
          <>
            <StandardTable
              selection={selection}
              setSelection={setSelection}
              indeterminate={indeterminate}
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Edición"
              items={
                drivers?.listado?.map((el: DriverListEntity) => ({
                  ...el,
                  estado: el?.activo ? "Activo" : "Inactivo",
                  equipos: el?.equipos?.length ? (el?.equipos ?? []).join(", ") : "-",
                })) ?? []
              }
              headers={driversHeaderElements}
              actionElements={getActionElements}
              noActions={!hasWriteAccess(PermissionCode.DRIVERS)}
              noSelection={!hasWriteAccess(PermissionCode.DRIVERS)}
            />

            {drivers?.totalPages && drivers?.totalPages > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={drivers?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>

      <Suspense>
        <ConfirmModalDynamic
          openConfirm={openConfirm}
          setOpenConfirm={setOpenConfirmModal}
          title={`Eliminar fleteros`}
          subtitle={`¿Desea eliminar los fleteros (${selection?.length}) seleccionados? No podrá deshacer esta acción después.`}
          loadingAccept={loadingDeleteDrivers}
          handleAccept={handleDelete}
        />
      </Suspense>
    </Flex>
  );
};

const ConfirmModalDynamic = lazy(() =>
  import("@/components/modal/ConfirmModal").then((el) => ({
    default: el["ConfirmModal"],
  })),
);
